import {FC, useCallback} from 'react';
import {useState} from 'react';
import {styled} from 'styled-components';
import {useLocation} from 'wouter';

import {MEDIA_QUERY_TABLET_AND_DESKTOP} from '@shared_web/breakpoints';
import {Button} from '@shared_web/components/core/button';
import {Input} from '@shared_web/components/core/input';
import {showAlert} from '@shared_web/components/core/notifications';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';

import {PageWrapper} from '@mavismart_frontend/components/common_styles';
import {Translate, translate} from '@mavismart_frontend/components/translate';
import {icon_maviflex_logo_red_and_grey} from '@mavismart_frontend/icons/icon_maviflex_logo_red_and_grey';
import {icon_mavismart_logo_red_and_grey} from '@mavismart_frontend/icons/icon_mavismart_logo_red_and_grey';
import {apiCall} from '@mavismart_frontend/lib/api';
import {BUTTON_THEME, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {getUserLanguage, useAccount} from '@mavismart_frontend/stores/account_store';
import {setLoginInfo} from '@mavismart_frontend/stores/login_info_store';

export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email) {
    showAlert(translate('enter_email', getUserLanguage(undefined)));
    return;
  }
  return emailRegex.test(email);
};

export const LoginPage: FC = () => {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState<string>('');

  const account = useAccount();
  const [loading, setLoading] = useState(false);
  const lang = getUserLanguage(account);

  const handleLogin = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      isValidEmail(email);

      try {
        setLoading(true);
        const {success, nextCodeAt} = await apiCall('POST /code', {email});

        if (success) {
          setLoginInfo({email, nextCodeAt: nextCodeAt ?? ''});
          navigate('/account-verification');
        } else {
          throw new Error('Not successful');
        }
      } catch {
        showAlert(translate('generic_error', getUserLanguage(account)));
      } finally {
        setLoading(false);
      }
    },
    [email, account, navigate]
  );

  return (
    <PageOuter>
      <PageInner>
        <WrapperLogo>
          <SvgIcon icon={icon_mavismart_logo_red_and_grey} width={300} />
          <Title>
            <Translate value="welcome_to_the_app" />
          </Title>
        </WrapperLogo>

        <Description>
          <Translate value={'enter_identifier_below'} />
        </Description>

        <Form onSubmit={handleLogin}>
          <div>
            <Input
              type="email"
              autoComplete="email"
              name="signup-email"
              width="100%"
              value={email}
              syncState={setEmail}
              placeholder={translate('email', lang)}
            />
          </div>
          <Flex $column $gap="xxl" $width="100%" $align="center">
            <Button themeOverrides={BUTTON_THEME} expand type="submit" loading={loading}>
              <Translate value={'login'} />
            </Button>
            <Mention $align="center">
              <div>{translate('powered_by', lang)}</div>
              <SvgIcon icon={icon_maviflex_logo_red_and_grey} height={26} />
            </Mention>
          </Flex>
        </Form>
      </PageInner>
    </PageOuter>
  );
};

LoginPage.displayName = 'LoginPage';

const PageOuter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100%;
`;

const PageInner = styled(PageWrapper)`
  width: 100%;
  max-width: 400px;
  justify-content: flex-start;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
`;

const Title = styled.h1`
  text-align: center;
  font-size: 28px;
  margin-bottom: 14px;
  line-height: 28px;
  color: ${TEXT_MAIN_COLOR};
`;

const Description = styled.p`
  font-size: 1rem;
  color: #666;
  font-size: 14px;
  margin-bottom: 7px;
  color: ${TEXT_MAIN_COLOR};
  ${MEDIA_QUERY_TABLET_AND_DESKTOP} {
  }
`;

const WrapperLogo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  svg {
    height: 150px;
    ${MEDIA_QUERY_TABLET_AND_DESKTOP} {
      height: 300px;
    }
  }
`;

const Mention = styled(Flex)`
  text-align: center;
  line-height: 100%;
  color: #8c8d8d;
  letter-spacing: 0.25px;
`;
